// Central API client.
//
// Base URL: VITE_API_BASE env var (defaults to '/api').
// In dev, Vite proxies /api → http://127.0.0.1:8090 so no CORS needed.
//
// Auth transport: httpOnly 'dash_session' cookie managed by the backend.
// The frontend never reads or writes the cookie directly — all requests send
// `credentials: 'include'` so the browser attaches the cookie automatically.
//
// On any 401 response: redirect to /login (unless already there, to avoid loops).

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

/** Full URL for a binary/non-JSON endpoint (e.g. the media proxy) — for use
 *  directly as an <img>/<audio> src, or a raw `fetch` when the response isn't
 *  JSON (apiFetch always calls res.json() on success). */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

function handle401(body: unknown): never {
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
  // A typed 401 (an ApiError), so callers can tell an authoritative "not signed in" from a transient failure.
  throw new ApiError(401, body);
}

/** True only for an authoritative "this session is not valid" answer (401), never for a network error, 429 or 5xx. */
export function isAuthFailure(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // Only declare a JSON content-type when we actually send a body. Setting it
  // on a body-less POST (claim/release/logout/resume/takeover) makes Fastify
  // reject the request with FST_ERR_CTP_EMPTY_JSON_BODY (400: "Body cannot be
  // empty when content-type is set to 'application/json'").
  const hasBody = init.body !== undefined && init.body !== null;
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include', // always send the session cookie
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers as Record<string, string> | undefined),
    },
  });

  if (res.status === 401) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* no body */ }
    handle401(body);
  }

  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = null; }
    const err = new ApiError(res.status, body);
    throw err;
  }

  return res.json() as Promise<T>;
}

/** Backend Pack Phase 7 — multipart upload with progress. Plain `fetch` has no
 *  reliable cross-browser upload-progress event, so this one call uses
 *  XMLHttpRequest instead; every other endpoint keeps using apiFetch above. */
export function apiUploadFetch<T>(
  path: string,
  form: FormData,
  onProgress?: (pct: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`);
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status === 401) { try { handle401(null); } catch (e) { reject(e); } return; }
      let body: unknown = null;
      try { body = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { body = null; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T);
      else reject(new ApiError(xhr.status, body));
    };
    xhr.onerror = () => reject(new ApiError(0, null));
    xhr.send(form);
  });
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API error ${status}`);
  }

  get errorCode(): string | null {
    if (
      this.body &&
      typeof this.body === 'object' &&
      'error' in this.body &&
      typeof (this.body as Record<string, unknown>)['error'] === 'string'
    ) {
      return (this.body as Record<string, string>)['error'];
    }
    return null;
  }

  get metaCode(): number | null {
    if (!this.body || typeof this.body !== 'object') return null;
    const code = (this.body as Record<string, unknown>)['code'];
    return typeof code === 'number' && Number.isFinite(code) ? code : null;
  }

  get failureReason(): string | null {
    if (!this.body || typeof this.body !== 'object') return null;
    const reason = (this.body as Record<string, unknown>)['reason'];
    return typeof reason === 'string' ? reason : null;
  }

  get failureState(): string | null {
    if (!this.body || typeof this.body !== 'object') return null;
    const state = (this.body as Record<string, unknown>)['failureState'];
    return typeof state === 'string' ? state : null;
  }

  get failureAction(): string | null {
    if (!this.body || typeof this.body !== 'object') return null;
    const action = (this.body as Record<string, unknown>)['failureAction'];
    return typeof action === 'string' ? action : null;
  }

  get retryable(): boolean | null {
    if (!this.body || typeof this.body !== 'object') return null;
    const retryable = (this.body as Record<string, unknown>)['retryable'];
    return typeof retryable === 'boolean' ? retryable : null;
  }

  get deliveryUncertain(): boolean {
    if (!this.body || typeof this.body !== 'object') return false;
    return (this.body as Record<string, unknown>)['deliveryUncertain'] === true;
  }
}
